// @flow
import * as React from 'react';
import invariant from 'invariant';
import type { CurrentUser } from 'api/currentUser';
import type { RouterHistory } from 'react-router';
import type {
  BlockType,
  PageContentBlock,
  SidebarContentBlock,
  RichTextBlockData,
  ImageBlockData,
  BlockquoteBlockData,
  GradCtaHeroBlockData,
  CtaHeroBlockData,
  StickyHeaderLinksBlockData,
  StickyHeaderLinksGradBlockData,
  AccordionBlockData,
  AnchorBlockData,
  VideoBlockData,
  ThreeStepCtaBlockData,
  TwoTilesBlockData,
  FreeCodeBlockData,
  EmailCampaignSignupSingleBlockData,
  EmailCampaignSignupDoubleBlockData,
  ThreeSectionSliderBlockData,
  CtaPromoBlockData,
  RandomGradProgramsBlockData,
  RecommendedActionsBlockData,
  FourArticlesHighlightBlockData,
  CategoryHighlightBlockData,
  IdealistDaysMapBlockData,
  SplitContentBlockData,
  FlashCardQuizBlockData,
  PersonaQuizBlockData,
  TileGridBlockData,
  ImageGalleryBlockData,
  FormBuilderBlockData
} from 'api/cms';
import type { PublicPage, Subsite } from 'api/website';
import Box from 'components/Box';
import {
  ImageBlock,
  RichTextBlock,
  BlockquoteBlock,
  StickyHeaderLinksBlock,
  StickyHeaderLinksGradBlock,
  AccordionBlock,
  CallToActionHeroBlock,
  GradCallToActionHeroBlock,
  ThreeStepCtaBlock,
  TwoTilesBlock,
  AnchorBlock,
  VideoBlock,
  FreeCodeBlock,
  EmailCampaignSignupSingleBlock,
  EmailCampaignSignupDoubleBlock,
  ThreeSectionSliderBlock,
  CtaPromoBlock,
  FourArticlesHighlightBlock,
  RandomGradProgramsBlock,
  RecommendedActionsBlock,
  CategoryHighlightBlock,
  IdealistDaysMapBlock,
  SplitContentBlock,
  ImageGalleryBlock,
  SupportContactFormBlock,
  FlashCardQuizBlock,
  PersonaQuizBlock,
  TileGridBlock,
  DonateBlock,
  GradFairGradProgramLinksBlock,
  FormBuilderBlock
} from 'components/Cms';
import {
  AmpRichTextBlock,
  AmpImageBlock,
  AmpBlockquoteBlock,
  // AmpFreeCodeBlock,
  AmpAccordionBlock,
  AmpVideoBlock,
  AmpLinksBlock,
  AmpHeroBlock,
  AmpSplitBlock,
  AmpCardsBlock,
  AmpRandomGradProgramsBlock,
  AmpTileGridBlock,
  AmpRecommendedActionsBlock
} from 'components/Cms/Amp';

const blocksWithBackground = [
  'FLASH_CARD_QUIZ',
  'TWO_TILES',
  'RANDOM_GRAD_PROGRAMS',
  'RECOMMENDED_ACTIONS'
];

export const hasBackground = ({
  block,
  fullWidthLayout
}: {
  block: PageContentBlock,
  fullWidthLayout?: boolean
}) =>
  (blocksWithBackground.includes(block.type) ||
    (block.type === 'SPLIT_CONTENT' &&
      block.data.appearance === 'OVERLAPPING') ||
    (block.type === 'THREE_STEP_CTA' &&
      ['One', 'Two', 'Three'].some(
        step => block.data[`step${step}Url`] && block.data.imageSize === 'SMALL'
      ))) &&
  fullWidthLayout;

type BlockWidth = 'GRID' | 'INSET' | 'NARROW' | 'FULL';

const BLOCKS: {
  [BlockType]: {|
    invariant: Array<{ prop: string, msg: string }>,
    width: BlockWidth | (Object => BlockWidth),
    Component: any,
    ampDataMap: Function,
    AmpComponent: any
  |}
} = {
  RICHTEXT: {
    invariant: [{ prop: 'html', msg: 'Html data required for rich text' }],
    width: 'INSET',
    Component: RichTextBlock,
    ampDataMap: (data: RichTextBlockData) => data,
    AmpComponent: AmpRichTextBlock
  },
  IMAGE: {
    invariant: [
      { prop: 'image', msg: 'Image data required for image' },
      { prop: 'width', msg: 'width required for image' }
    ],
    width: ({ width }: { width: 'WIDE' | 'NARROW' }) =>
      width === 'WIDE' ? 'GRID' : 'NARROW',
    Component: ImageBlock,
    ampDataMap: (data: ImageBlockData) => data,
    AmpComponent: AmpImageBlock
  },
  BLOCKQUOTE: {
    invariant: [{ prop: 'quote', msg: 'Quote required for blockquote' }],
    width: 'NARROW',
    Component: BlockquoteBlock,
    ampDataMap: (data: BlockquoteBlockData) => data,
    AmpComponent: AmpBlockquoteBlock
  },
  CTA_HERO: {
    invariant: [
      { prop: 'text', msg: 'Text data required for call to action' },
      { prop: 'image', msg: 'Image data required for call to action' }
    ],
    width: 'FULL',
    Component: CallToActionHeroBlock,
    ampDataMap: (data: CtaHeroBlockData) => data,
    AmpComponent: AmpHeroBlock
  },
  GRAD_CTA_HERO: {
    invariant: [
      { prop: 'text', msg: 'Text data required for call to action' },
      { prop: 'image', msg: 'Image data required for call to action' }
    ],
    width: 'FULL',
    Component: GradCallToActionHeroBlock,
    ampDataMap: (data: GradCtaHeroBlockData) => ({ ...data, isGrad: true }),
    AmpComponent: AmpHeroBlock
  },
  STICKY_HEADER_LINKS: {
    invariant: [
      { prop: 'links', msg: 'Links required for sticky header links' }
    ],
    width: 'FULL',
    Component: StickyHeaderLinksBlock,
    ampDataMap: (data: StickyHeaderLinksBlockData) => data,
    AmpComponent: AmpLinksBlock
  },
  STICKY_HEADER_LINKS_GRAD: {
    invariant: [
      { prop: 'links', msg: 'Links required for sticky header links' }
    ],
    width: 'FULL',
    Component: StickyHeaderLinksGradBlock,
    ampDataMap: (data: StickyHeaderLinksGradBlockData) => ({
      ...data,
      isGrad: true
    }),
    AmpComponent: AmpLinksBlock
  },
  ACCORDION: {
    invariant: [{ prop: 'sections', msg: 'Sections required for accordion' }],
    width: 'INSET',
    Component: AccordionBlock,
    ampDataMap: (data: AccordionBlockData) => data,
    AmpComponent: AmpAccordionBlock
  },
  ANCHOR: {
    invariant: [{ prop: 'anchor', msg: 'Anchor required for anchor' }],
    width: 'GRID',
    Component: AnchorBlock,
    ampDataMap: (data: AnchorBlockData) => data,
    AmpComponent: null
  },
  VIDEO: {
    invariant: [
      { prop: 'videoUrl', msg: 'URL required for video' },
      { prop: 'width', msg: 'width required for video' }
    ],
    width: ({ width }: { width: 'WIDE' | 'NARROW' }) =>
      width === 'WIDE' ? 'INSET' : 'NARROW',
    Component: VideoBlock,
    ampDataMap: (data: VideoBlockData) => data,
    AmpComponent: AmpVideoBlock
  },
  THREE_STEP_CTA: {
    invariant: [
      {
        prop: 'mainHeadline',
        msg: 'Main Heading required for Three Step Call-to-Action'
      }
    ],
    width: 'GRID',
    Component: ThreeStepCtaBlock,
    ampDataMap: (data: ThreeStepCtaBlockData) => ({
      headline: data.mainHeadline,
      subheadline: data.mainText,
      buttonText: data.buttonText,
      buttonUrl: data.buttonUrl,
      cards: [
        {
          image: data.stepOneImage,
          headline: data.stepOneHeadline,
          text: data.stepOneText,
          url: data.stepOneUrl
        },
        {
          image: data.stepTwoImage,
          headline: data.stepTwoHeadline,
          text: data.stepTwoText,
          url: data.stepTwoUrl
        },
        {
          image: data.stepThreeImage,
          headline: data.stepThreeHeadline,
          text: data.stepThreeText,
          url: data.stepThreeUrl
        }
      ]
    }),
    AmpComponent: AmpCardsBlock
  },
  TWO_TILES: {
    invariant: [
      {
        prop: 'tileOneHeadline',
        msg: 'Tile One Heading required for Two Tiles'
      },
      {
        prop: 'tileTwoHeadline',
        msg: 'Tile Two Heading required for Two Tiles'
      }
    ],
    width: 'GRID',
    Component: TwoTilesBlock,
    ampDataMap: (data: TwoTilesBlockData) => {
      const buttonOne =
        data.tileOneButtonText && data.tileOneButtonUrl
          ? {
              text: data.tileOneButtonText,
              data: {
                ur: data.tileOneButtonUrl
              }
            }
          : null;
      const buttonTwo =
        data.tileTwoButtonText && data.tileTwoButtonUrl
          ? {
              text: data.tileTwoButtonText,
              data: {
                ur: data.tileTwoButtonUrl
              }
            }
          : null;
      return {
        sections: [
          {
            type: 'TEXT',
            data: {
              headline: data.tileOneHeadline,
              html: data.tileOneText,
              button: buttonOne
            }
          },
          {
            type: 'TEXT',
            data: {
              headline: data.tileOneHeadline,
              html: data.tileOneText,
              button: buttonTwo
            }
          }
        ]
      };
    },
    AmpComponent: AmpSplitBlock
  },
  FREE_CODE: {
    invariant: [{ prop: 'code', msg: 'Code required' }],
    width: 'INSET',
    Component: FreeCodeBlock,
    ampDataMap: ({ code: html, isVisible }: FreeCodeBlockData) => ({
      html,
      isVisible
    }),
    // AmpComponent: AmpFreeCodeBlock
    AmpComponent: null
  },
  EMAIL_CAMPAIGN_SIGNUP_SINGLE: {
    invariant: [
      {
        prop: 'headline',
        msg: 'Heading required for Email Campaign Signup Single'
      },
      {
        prop: 'buttonText',
        msg: 'Button Text required for Email Campaign Signup Single'
      },
      {
        prop: 'campaignId',
        msg: 'Campaign ID required for Email Campaign Signup Single'
      },
      {
        prop: 'listId',
        msg: 'List ID required for Email Campaign Signup Single'
      }
    ],
    width: 'GRID',
    Component: EmailCampaignSignupSingleBlock,
    ampDataMap: (data: EmailCampaignSignupSingleBlockData) => ({
      image: data.bgImage,
      text: data.headline,
      buttonText: data.buttonText,
      isEmailSignup: true
    }),
    AmpComponent: AmpHeroBlock
  },
  EMAIL_CAMPAIGN_SIGNUP_DOUBLE: {
    invariant: [
      {
        prop: 'headline',
        msg: 'Heading required for Email Campaign Signup Double'
      },
      {
        prop: 'toggleTextOne',
        msg: 'Toggle Text One required for Email Campaign Signup Double'
      },
      {
        prop: 'buttonTextOne',
        msg: 'Button Text One required for Email Campaign Signup Double'
      },
      {
        prop: 'campaignIdOne',
        msg: 'Campaign ID One required for Email Campaign Signup Double'
      },
      {
        prop: 'listIdOne',
        msg: 'List ID One required for Email Campaign Signup Double'
      },
      {
        prop: 'toggleTextTwo',
        msg: 'Toggle Text Two required for Email Campaign Signup Double'
      },
      {
        prop: 'buttonTextTwo',
        msg: 'Button Text Two required for Email Campaign Signup Double'
      },
      {
        prop: 'campaignIdTwo',
        msg: 'Campaign ID Two required for Email Campaign Signup Double'
      },
      {
        prop: 'listIdTwo',
        msg: 'List ID Two required for Email Campaign Signup Double'
      }
    ],
    width: 'GRID',
    Component: EmailCampaignSignupDoubleBlock,
    ampDataMap: (data: EmailCampaignSignupDoubleBlockData) => ({
      image: data.bgImage,
      text: data.headline,
      buttonText: data.buttonTextOne,
      isEmailSignup: true
    }),
    AmpComponent: AmpHeroBlock
  },
  FORM_BUILDER: {
    invariant: [],
    width: 'GRID',
    Component: FormBuilderBlock,
    ampDataMap: (data: FormBuilderBlockData) => ({
      image: data.bgImage,
      text: data.headline,
      buttonText: data.submitButtonText,
      isEmailSignup: true
    }),
    AmpComponent: AmpHeroBlock
  },
  THREE_SECTION_SLIDER: {
    invariant: [
      {
        prop: 'headline',
        msg: 'Heading required for Three Section Slider'
      },
      {
        prop: 'sections',
        msg: 'Sections required for Three Section Slider'
      }
    ],
    width: 'INSET',
    Component: ThreeSectionSliderBlock,
    ampDataMap: (data: ThreeSectionSliderBlockData) => ({
      headline: data.headline,
      imageSize: data.imageSize,
      cards: data.sections.map(section => ({
        text: section.content,
        image: section.image
      }))
    }),
    AmpComponent: AmpCardsBlock
  },
  CTA_PROMO: {
    invariant: [
      {
        prop: 'headline',
        msg: 'Heading required for CTA Promo'
      },
      {
        prop: 'text',
        msg: 'Text required for CTA Promo'
      }
    ],
    width: 'GRID',
    Component: CtaPromoBlock,
    ampDataMap: (data: CtaPromoBlockData) => ({
      sections: [
        {
          type: 'TEXT',
          data: {
            headline: data.headline,
            text: data.text,
            button: {
              type: 'URL',
              text: data.primaryButtonText,
              data: {
                url: data.primaryButtonUrl
              }
            }
          }
        },
        {
          type: 'IMAGE',
          data: {
            image: data.image
          }
        }
      ]
    }),
    AmpComponent: AmpSplitBlock
  },
  FOUR_ARTICLES_HIGHLIGHT: {
    invariant: [],
    width: 'GRID',
    Component: FourArticlesHighlightBlock,
    ampDataMap: (data: FourArticlesHighlightBlockData) => ({
      headline: data.headline,
      cards: ['One', 'Two', 'Three', 'Four']
        .filter(page => data[`page${page}`])
        .map(page => ({
          image: data[`page${page}`].image,
          headline: data[`page${page}`].title,
          url: data[`page${page}`].url
        })),
      maxLength: 4
    }),
    AmpComponent: AmpCardsBlock
  },
  RANDOM_GRAD_PROGRAMS: {
    invariant: [],
    width: 'GRID',
    Component: RandomGradProgramsBlock,
    ampDataMap: (data: RandomGradProgramsBlockData) => data,
    AmpComponent: AmpRandomGradProgramsBlock
  },
  RECOMMENDED_ACTIONS: {
    invariant: [],
    width: 'GRID',
    Component: RecommendedActionsBlock,
    ampDataMap: (data: RecommendedActionsBlockData) => data,
    AmpComponent: AmpRecommendedActionsBlock
  },
  CATEGORY_HIGHLIGHT: {
    invariant: [],
    width: 'GRID',
    Component: CategoryHighlightBlock,
    ampDataMap: (data: CategoryHighlightBlockData) => ({
      headline: data.headline,
      cards: data.categories.map(category => ({
        headline: category.name,
        image: category.image,
        text: category.description,
        body: (
          <>
            {category.pages
              ? category.pages.map(page => (
                  <Box
                    component="a"
                    href={page.url}
                    key={page.id}
                    display="block"
                  >
                    {page.title}
                  </Box>
                ))
              : null}
          </>
        )
      }))
    }),
    AmpComponent: AmpCardsBlock
  },
  IDEALIST_DAYS_MAP: {
    invariant: [],
    width: 'FULL',
    Component: IdealistDaysMapBlock,
    ampDataMap: (data: IdealistDaysMapBlockData) => {
      const text = data.instructions;
      const buttonText = data.buttonText || getText('Get Started');
      return {
        buttonText,
        text,
        label:
          data.mapType === 'DAYS'
            ? getText('Idealist Days')
            : getText('Mutual Aid Groups'),
        isMap: true
      };
    },
    AmpComponent: AmpHeroBlock
  },
  SPLIT_CONTENT: {
    invariant: [],
    width: 'GRID',
    Component: SplitContentBlock,
    ampDataMap: (data: SplitContentBlockData) => data,
    AmpComponent: AmpSplitBlock
  },
  IMAGE_GALLERY: {
    invariant: [{ prop: 'images', msg: 'Images required for Image Gallery' }],
    width: 'GRID',
    Component: ImageGalleryBlock,
    ampDataMap: (data: ImageGalleryBlockData) => ({
      imageSize: 'LARGE',
      cards: data.images.map(image => ({
        image
      })),
      seeMoreText: getText('See All Images')
    }),
    AmpComponent: AmpCardsBlock
  },
  SUPPORT_CONTACT_FORM: {
    invariant: [],
    width: 'GRID',
    Component: SupportContactFormBlock,
    ampDataMap: () => ({
      text: getText('Need Help?'),
      buttonText: getText('Contact Us')
    }),
    AmpComponent: AmpHeroBlock
  },
  FLASH_CARD_QUIZ: {
    invariant: [],
    width: 'GRID',
    Component: FlashCardQuizBlock,
    ampDataMap: (data: FlashCardQuizBlockData) => ({
      text: data.cards[0].question,
      image: data.cards[0].image,
      buttonText: getText('View Quiz')
    }),
    AmpComponent: AmpHeroBlock
  },
  PERSONA_QUIZ: {
    invariant: [],
    width: 'GRID',
    Component: PersonaQuizBlock,
    ampDataMap: (data: PersonaQuizBlockData) => ({
      text: data.questions[0].title,
      image: data.questions[0].image,
      buttonText: getText('View Quiz')
    }),
    AmpComponent: AmpHeroBlock
  },
  TILE_GRID: {
    invariant: [],
    width: 'GRID',
    Component: TileGridBlock,
    ampDataMap: (data: TileGridBlockData) => data,
    AmpComponent: AmpTileGridBlock
  },
  DONATE: {
    invariant: [],
    width: 'FULL',
    Component: DonateBlock,
    ampDataMap: data => data,
    AmpComponent: DonateBlock
  },
  GRAD_FAIR_GRAD_PROGRAM_LINKS: {
    invariant: [],
    width: 'INSET',
    Component: GradFairGradProgramLinksBlock,
    ampDataMap: data => data,
    AmpComponent: GradFairGradProgramLinksBlock
  }
};

export const AmpContentBlock = ({
  block,
  loggedIn,
  hasGradProgram,
  pageUrl
}: {
  block: PageContentBlock,
  loggedIn: boolean,
  hasGradProgram: boolean,
  pageUrl: string
}) => {
  if (!BLOCKS[block.type]) {
    return null;
  }
  const { ampDataMap, AmpComponent } = BLOCKS[block.type];
  return AmpComponent && ampDataMap ? (
    <AmpComponent
      loggedIn={loggedIn}
      hasGradProgram={hasGradProgram}
      pageUrl={pageUrl}
      blockId={block.id}
      data={ampDataMap(block.data)}
    />
  ) : null;
};

export default ({
  content,
  blocks,
  block,
  loggedIn,
  currentUser,
  showModal,
  pushFlashMessage,
  updateCurrentUser,
  pageUrl,
  locale,
  subsite,
  history,
  hasGradProgram,
  isSidebar,
  isPageHeader,
  homePageUrl,
  fullWidthLayout,
  anchor,
  offsetAnchors,
  parallax,
  index,
  pageUpdated,
  gMapsErrored,
  setExposeSiteNav
}: {|
  content: PublicPage,
  blocks?: Array<PageContentBlock>,
  block: PageContentBlock | SidebarContentBlock,
  loggedIn: boolean,
  currentUser: ?CurrentUser,
  showModal: Function,
  pushFlashMessage: Function,
  updateCurrentUser: Function,
  pageUrl: string,
  locale: string,
  subsite: Subsite,
  history: RouterHistory,
  hasGradProgram: boolean,
  isSidebar: boolean,
  isPageHeader?: boolean,
  homePageUrl: string,
  fullWidthLayout?: boolean,
  anchor?: PageContentBlock,
  offsetAnchors?: boolean,
  parallax?: boolean,
  index: number,
  pageUpdated: string,
  gMapsErrored: boolean,
  setExposeSiteNav: Function
|}) => {
  if (BLOCKS[block.type]) {
    const { width, Component, ...contentBlock } = BLOCKS[block.type];

    contentBlock.invariant.forEach(item => {
      if (block.data[item.prop]) {
        invariant(block.data[item.prop], item.msg);
      }
    });

    const widthValue = typeof width === 'function' ? width(block.data) : width;

    const blockHasBackground = hasBackground({ block, fullWidthLayout });

    return {
      id: block.id,
      type: block.type,
      width: widthValue,
      mustNotHaveDivider:
        block.type === 'SPLIT_CONTENT' &&
        block.data.appearance === 'FULL_WIDTH',
      padding:
        (blocks &&
          ((block.type === 'SPLIT_CONTENT' &&
            block.data.appearance === 'FULL_WIDTH' &&
            fullWidthLayout) ||
            [
              'GRAD_CTA_HERO',
              'CTA_HERO',
              'STICKY_HEADER_LINKS_GRAD',
              'STICKY_HEADER_LINKS',
              'IDEALIST_DAYS_MAP',
              'DONATE'
            ].includes(block.type))) ||
        blockHasBackground
          ? 'NONE'
          : 'DEFAULT',
      hasBackground: blockHasBackground,
      isHidden: block.type === 'FREE_CODE' && !block.data.isVisible,
      component: (
        <>
          {anchor && anchor.type === 'ANCHOR' && (
            <Box
              position="absolute"
              top={offsetAnchors ? [0, null, null, -56] : 0}
            >
              <AnchorBlock data={anchor.data} />
            </Box>
          )}
          <div>
            <Component
              content={content}
              loggedIn={loggedIn}
              currentUser={currentUser}
              showModal={showModal}
              pushFlashMessage={pushFlashMessage}
              updateCurrentUser={updateCurrentUser}
              pageUrl={pageUrl}
              locale={locale}
              subsite={subsite}
              history={history}
              hasGradProgram={hasGradProgram}
              isSidebar={isSidebar}
              isPageHeader={
                isPageHeader &&
                ['CTA_HERO', 'GRAD_CTA_HERO', 'DONATE'].includes(block.type)
              }
              homePageUrl={homePageUrl}
              fullWidthLayout={fullWidthLayout}
              parallax={parallax}
              blockId={block.id}
              data={block.data}
              blockIndex={index}
              pageUpdated={pageUpdated}
              gMapsErrored={gMapsErrored}
              setExposeSiteNav={setExposeSiteNav}
            />
          </div>
        </>
      )
    };
  }
  throw new Error(`Expected block type, got ${block.type}`);
};
